import { useCallback } from 'react';

import sum from 'lodash/sum';

import { BUCKET_COUNT } from '../constants';
import {
  WeightedItem,
  changeItemWeightAndDistributeRemaining,
  distributeWeightEvenly,
} from './useWeightedItems';

// We translate treatment weights into a space of 10_000 buckets to ensure two-digit precision
// after the comma for given percentage values

export function scaleWeights<T extends { weight?: number | null }>(
  treatments: T[],
): T[] {
  const sumOfWeights = sum(treatments.map(t => t.weight || 0));
  if (sumOfWeights === 0) {
    // All weights are zero — distribute evenly using the largest remainder method
    const base = Math.floor(BUCKET_COUNT / treatments.length);
    const remainder = BUCKET_COUNT - base * treatments.length;
    return treatments.map((t, i) => ({
      ...t,
      weight: base + (i < remainder ? 1 : 0),
    }));
  }
  if (sumOfWeights !== BUCKET_COUNT) {
    const exactWeights = treatments.map(
      t => ((t.weight || 0) * BUCKET_COUNT) / sumOfWeights,
    );
    const floored = exactWeights.map(w => Math.floor(w));
    let remainder = BUCKET_COUNT - sum(floored);

    // Distribute the remainder one unit at a time to the items with the
    // largest fractional parts (largest remainder method).
    const fractionals = exactWeights
      .map((w, i) => ({ index: i, fraction: w - floored[i] }))
      .sort((a, b) => b.fraction - a.fraction);

    for (const { index } of fractionals) {
      if (remainder <= 0) break;
      floored[index] += 1;
      remainder -= 1;
    }

    return treatments.map((t, i) => ({
      ...t,
      weight: floored[i],
    }));
  }

  return treatments;
}

export const useTreatmentWeightsChange = <T extends WeightedItem>({
  treatments,
  onChange,
}: {
  treatments: T[];
  onChange: (treatments: T[]) => void;
}) => {
  const handleWeightChange = useCallback(
    (index: number) => {
      return (v?: number) => {
        const newTreatments = changeItemWeightAndDistributeRemaining(
          treatments,
          index,
          v ?? 0,
          BUCKET_COUNT,
        );

        onChange(newTreatments);
      };
    },
    [onChange, treatments],
  );

  const balanceWeights = useCallback(() => {
    const newTreatments = distributeWeightEvenly(treatments, BUCKET_COUNT);
    onChange(newTreatments);
  }, [onChange, treatments]);

  return {
    handleWeightChange,
    balanceWeights,
  };
};
